import React, { useState } from "react";
import { Typography, Switch, Divider } from "antd";
import { toast } from "react-toastify";
import { updateCard } from "../../api/AdminRequest";

const { Title } = Typography;

const featureConfig = [
  {
    key: "googleWallet",
    label: "Google Wallet",
    description: "Allow users to add this card to Google Wallet",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#4285F4" opacity="0.12" />
        <path
          d="M12 5C8.13 5 5 8.13 5 12s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7zm0 12.5c-3.03 0-5.5-2.47-5.5-5.5S8.97 6.5 12 6.5s5.5 2.47 5.5 5.5-2.47 5.5-5.5 5.5z"
          fill="#4285F4"
        />
        <path d="M12.5 8H11v5l4.25 2.55.75-1.23-3.5-2.07V8z" fill="#4285F4" />
      </svg>
    ),
    accentColor: "#4285F4",
    getValue: (cardData) => cardData?.googleWallet?.show ?? false,
    buildPayload: (checked, cardData) => ({
      googleWallet: { ...(cardData?.googleWallet || {}), show: checked },
    }),
  },
  {
    key: "appleWallet",
    label: "Apple Wallet",
    description: "Allow users to add this card to Apple Wallet",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#1C1C1E" opacity="0.10" />
        <path
          d="M17.05 12.536c-.03-2.608 2.13-3.87 2.228-3.932-1.215-1.776-3.105-2.02-3.776-2.044-1.61-.163-3.148.951-3.963.951-.815 0-2.078-.93-3.418-.904-1.757.026-3.381 1.027-4.283 2.605-1.826 3.172-.469 7.876 1.31 10.454.872 1.258 1.909 2.668 3.273 2.616 1.318-.052 1.815-.849 3.41-.849 1.594 0 2.043.849 3.432.822 1.414-.024 2.308-1.278 3.172-2.54.999-1.453 1.412-2.862 1.436-2.935-.031-.014-2.75-1.054-2.78-4.244z"
          fill="#1C1C1E"
        />
        <path
          d="M14.396 4.61c.72-.876 1.206-2.094 1.074-3.31-1.038.042-2.295.692-3.038 1.566-.668.773-1.254 2.013-1.097 3.201 1.158.09 2.338-.588 3.061-1.457z"
          fill="#1C1C1E"
        />
      </svg>
    ),
    accentColor: "#1C1C1E",
    getValue: (cardData) => cardData?.appleWallet?.show ?? false,
    buildPayload: (checked, cardData) => ({
      appleWallet: { ...(cardData?.appleWallet || {}), show: checked },
    }),
  },
  {
    key: "gallery",
    label: "Gallery",
    description: "Show highlight photos gallery on the card",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#8B5CF6" opacity="0.12" />
        <rect x="3" y="3" width="7" height="7" rx="1.5" fill="#8B5CF6" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" fill="#8B5CF6" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" fill="#8B5CF6" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" fill="#8B5CF6" />
      </svg>
    ),
    accentColor: "#8B5CF6",
    getValue: (cardData) => cardData?.checkHighlight ?? false,
    buildPayload: (checked) => ({ checkHighlight: checked }),
  },
  {
    key: "personalInfo",
    label: "Personal Info",
    description: "Display personal profile info on the card",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#10B981" opacity="0.12" />
        <circle cx="12" cy="8" r="3.5" fill="#10B981" />
        <path
          d="M5 19c0-3.866 3.134-7 7-7s7 3.134 7 7"
          stroke="#10B981"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
    accentColor: "#10B981",
    getValue: (cardData) => cardData?.checkPfCard ?? false,
    buildPayload: (checked) => ({ checkPfCard: checked }),
  },
  {
    key: "exchangeContact",
    label: "Exchange Contact",
    description: "Let visitors share their contact details with the card owner",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#F59E0B" opacity="0.12" />
        <path
          d="M7 8h10M7 12h6"
          stroke="#F59E0B"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M16 15l3 3-3 3"
          stroke="#F59E0B"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19 18H5"
          stroke="#F59E0B"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
    accentColor: "#F59E0B",
    getValue: (cardData) => cardData?.exchangeContact?.show ?? false,
    buildPayload: (checked, cardData) => ({
      exchangeContact: { ...(cardData?.exchangeContact || {}), show: checked },
    }),
  },
];

export default function Features({ cardId, cardData: initialCardData }) {
  const [states, setStates] = useState(() => {
    const init = {};
    featureConfig.forEach((f) => {
      init[f.key] = f.getValue(initialCardData);
    });
    return init;
  });

  const [loading, setLoading] = useState({});

  const handleToggle = async (featureKey, checked) => {
    const feature = featureConfig.find((f) => f.key === featureKey);
    if (!feature) return;

    setLoading((prev) => ({ ...prev, [featureKey]: true }));

    try {
      const payload = feature.buildPayload(checked, initialCardData);
      await updateCard(cardId, payload);
      setStates((prev) => ({ ...prev, [featureKey]: checked }));
      toast.success(
        `${feature.label} ${checked ? "enabled" : "disabled"} successfully`,
        {
          position: "top-right",
          autoClose: 2500,
          theme: "dark",
        },
      );
    } catch (error) {
      toast.error(error.message || `Failed to update ${feature.label}`);
    } finally {
      setLoading((prev) => ({ ...prev, [featureKey]: false }));
    }
  };

  return (
    <div className="mt-6">
      <Title level={4} style={{ marginBottom: 4 }}>
        Card Features
      </Title>
      <p style={{ color: "#6B7280", marginBottom: 20, fontSize: 14 }}>
        Toggle features to enable or disable them on the card
      </p>

      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: 12,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {featureConfig.map((feature, index) => (
          <React.Fragment key={feature.key}>
            {index > 0 && (
              <Divider style={{ margin: 0, borderColor: "#F3F4F6" }} />
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                transition: "background 0.2s",
                background: states[feature.key]
                  ? `${feature.accentColor}08`
                  : "transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: states[feature.key]
                      ? `${feature.accentColor}18`
                      : "#F9FAFB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.2s",
                    flexShrink: 0,
                  }}
                >
                  {feature.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#111827",
                      lineHeight: 1.4,
                    }}
                  >
                    {feature.label}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6B7280",
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {feature.description}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: states[feature.key]
                      ? feature.accentColor
                      : "#9CA3AF",
                    minWidth: 40,
                    textAlign: "right",
                    transition: "color 0.2s",
                  }}
                >
                  {states[feature.key] ? "On" : "Off"}
                </span>
                <Switch
                  checked={states[feature.key]}
                  loading={loading[feature.key]}
                  onChange={(checked) => handleToggle(feature.key, checked)}
                  style={{
                    backgroundColor: states[feature.key]
                      ? feature.accentColor
                      : undefined,
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
